import React, { useMemo, useState } from 'react';
import { Header } from '../../components/Header';
import {
  Box, Container, Typography, Paper, Chip, Button, CircularProgress, Stack,
  Tooltip, IconButton,
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import InboxIcon from '@mui/icons-material/Inbox';
import HistoryIcon from '@mui/icons-material/History';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { gql, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';  // eslint-disable-line no-duplicate-imports
import { myLoyaltyCoupons } from '../../apollo/server';
import { FONT_BODY } from '../../utils/designTokens';

const MY_COUPONS = gql`${myLoyaltyCoupons}`;

const STATUS_META = {
  ACTIVE:     { bucket: 'ACTIVE', label: 'Active',  accent: '#16a34a', tint: '#dcfce7', border: '#86efac' },
  REINSTATED: { bucket: 'ACTIVE', label: 'Active',  accent: '#16a34a', tint: '#dcfce7', border: '#86efac' },
  USED:       { bucket: 'PAST',   label: 'Used',    accent: '#64748b', tint: '#f1f5f9', border: '#cbd5e1' },
  EXPIRED:    { bucket: 'PAST',   label: 'Expired', accent: '#dc2626', tint: '#fee2e2', border: '#fca5a5' },
};

function metaOf(status) {
  return STATUS_META[status] || STATUS_META.USED;
}

const FILTERS = [
  { key: 'ALL', label: 'All', icon: InboxIcon },
  { key: 'ACTIVE', label: 'Active', icon: CheckCircleOutlineIcon },
  { key: 'PAST', label: 'Past', icon: HistoryIcon },
];

function CouponTicket({ coupon }) {
  const [copied, setCopied] = useState(false);
  const meta = metaOf(coupon.status);
  const isActive = meta.bucket === 'ACTIVE';

  const copy = (e) => {
    e.stopPropagation();
    if (!coupon.code) return;
    navigator.clipboard?.writeText(coupon.code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        display: 'flex',
        bgcolor: '#fff',
        borderRadius: '18px',
        border: `1px solid ${isActive ? meta.border : '#e5e7eb'}`,
        overflow: 'hidden',
        transition: 'all 0.18s ease',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        opacity: isActive ? 1 : 0.78,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 10px 26px rgba(15,23,42,0.10)',
          borderColor: meta.accent,
        },
      }}
    >
      {/* Left ticket stub */}
      <Box sx={{
        flexShrink: 0,
        width: { xs: 100, sm: 120 },
        bgcolor: meta.tint,
        borderRight: `1.5px dashed ${meta.accent}55`,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        p: 1.5,
        position: 'relative',
        '&::before, &::after': {
          content: '""', position: 'absolute', right: -8,
          width: 16, height: 16,
          bgcolor: '#f8fafc',
          borderRadius: '50%', border: '1px solid #e5e7eb',
        },
        '&::before': { top: -8 },
        '&::after': { bottom: -8 },
      }}>
        <CardGiftcardIcon sx={{ color: meta.accent, fontSize: 22, mb: 0.5 }} />
        <Typography sx={{
          color: meta.accent, fontWeight: 900,
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          lineHeight: 1, letterSpacing: 0.2,
        }}>
          ${parseFloat(coupon.discountValue).toFixed(2)}
        </Typography>
        <Typography sx={{
          color: meta.accent, fontWeight: 700, fontSize: '0.65rem',
          mt: 0.25, letterSpacing: 0.5, textTransform: 'uppercase',
        }}>
          off
        </Typography>
      </Box>

      {/* Right content */}
      <Box sx={{ flex: 1, minWidth: 0, p: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Loyalty coupon
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.25 }}>
              <Typography sx={{
                fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700,
                color: '#0f172a', letterSpacing: 1.2,
              }}>
                {coupon.code}
              </Typography>
              {isActive && (
                <Tooltip title={copied ? 'Copied!' : 'Copy code'} placement="top">
                  <IconButton size="small" onClick={copy} sx={{ p: 0.25 }}>
                    {copied
                      ? <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#16a34a' }} />
                      : <ContentCopyIcon sx={{ fontSize: 14, color: '#94a3b8' }} />}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>
          <Chip
            label={meta.label}
            size="small"
            sx={{
              fontWeight: 700, fontSize: '0.7rem', height: 22,
              bgcolor: meta.tint, color: meta.accent,
              border: `1px solid ${meta.border}`,
            }}
          />
        </Stack>

        <Stack direction="row" alignItems="center" gap={0.4} sx={{ mt: 1 }}>
          <AccessTimeIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8' }}>
            {isActive
              ? `Expires ${new Date(coupon.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
              : coupon.usedAt
                ? `Used ${new Date(coupon.usedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : `Expired ${new Date(coupon.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
          </Typography>
        </Stack>

        {isActive && (
          <Stack direction="row" alignItems="flex-start" gap={0.75} sx={{ mt: 1.25, p: 1, borderRadius: '10px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: '#1d4ed8', mt: 0.15 }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#1e40af', lineHeight: 1.5 }}>
              Select this coupon from the <b>Loyalty Coupons</b> section at checkout to apply.
            </Typography>
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

function MyCoupons() {
  const { data, loading } = useQuery(MY_COUPONS, { fetchPolicy: 'network-only' });
  const coupons = data?.myLoyaltyCoupons ?? [];

  const [filter, setFilter] = useState('ALL');

  const buckets = useMemo(() => {
    const active = [], past = [];
    for (const c of coupons) {
      (metaOf(c.status).bucket === 'ACTIVE' ? active : past).push(c);
    }
    // Sort: active by soonest expiry; past by newest first.
    active.sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt));
    past.sort((a, b) => new Date(b.usedAt || b.expiresAt) - new Date(a.usedAt || a.expiresAt));
    return { active, past };
  }, [coupons]);

  const totalSavings = useMemo(
    () => buckets.active.reduce((s, c) => s + parseFloat(c.discountValue || 0), 0),
    [buckets.active]
  );

  const counts = {
    ALL: coupons.length,
    ACTIVE: buckets.active.length,
    PAST: buckets.past.length,
  };

  const dateStr = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Header />

      {/* Hero banner */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #FF3008 130%)',
        pt: { xs: 10, md: 12 },
        pb: { xs: 6, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: -120, right: -120,
          width: 380, height: 380, borderRadius: '50%',
          background: 'rgba(255,255,255,0.04)',
        },
        '&::after': {
          content: '""', position: 'absolute', bottom: -160, left: -100,
          width: 320, height: 320, borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        },
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2.5}>
            <Box sx={{
              width: { xs: 52, md: 60 }, height: { xs: 52, md: 60 }, borderRadius: '14px',
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <CardGiftcardIcon sx={{ color: '#fca5a5', fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: '1.6rem', md: '2.1rem' },
                color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em',
              }}>
                Coupon Wallet
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', mt: 0.4 }}>
                {dateStr} &middot; Loyalty coupons redeemed from your points
              </Typography>
            </Box>
            {buckets.active.length > 0 && (
              <Box sx={{
                px: 2.5, py: 1.25, borderRadius: '14px',
                background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.18)',
                textAlign: 'center', minWidth: 130,
              }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Available savings
                </Typography>
                <Typography sx={{ color: '#fff', fontSize: '1.6rem', fontWeight: 800, lineHeight: 1 }}>
                  ${totalSavings.toFixed(2)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: -4, pb: 6, position: 'relative', zIndex: 2 }}>
        {/* Filter pills */}
        {!loading && coupons.length > 0 && (
          <Box sx={{
            mb: 3, p: 1, bgcolor: '#fff', borderRadius: '16px',
            border: '1px solid #e5e7eb', display: 'inline-flex', gap: 0.5,
            boxShadow: '0 6px 18px rgba(15,23,42,0.06)',
          }}>
            {FILTERS.map(f => {
              const Icon = f.icon;
              const active = filter === f.key;
              return (
                <Box
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.75,
                    px: 2, py: 0.75, borderRadius: '12px', cursor: 'pointer',
                    bgcolor: active ? '#FF3008' : 'transparent',
                    color: active ? '#fff' : '#475569',
                    transition: 'all 0.15s ease',
                    '&:hover': { bgcolor: active ? '#FF3008' : '#f1f5f9' },
                  }}
                >
                  <Icon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                    {f.label}
                  </Typography>
                  <Box sx={{
                    bgcolor: active ? 'rgba(255,255,255,0.22)' : '#e5e7eb',
                    color: active ? '#fff' : '#475569',
                    fontSize: '0.7rem', fontWeight: 800,
                    px: 0.75, py: 0.05, borderRadius: '8px',
                    minWidth: 22, textAlign: 'center',
                  }}>
                    {counts[f.key]}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#FF3008' }} />
          </Box>
        )}

        {!loading && coupons.length === 0 && (
          <Box sx={{
            textAlign: 'center', py: 8, bgcolor: '#fff', borderRadius: '16px',
            border: '1px solid #e5e7eb',
          }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%',
              bgcolor: '#f3e8ff', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', mb: 2,
            }}>
              <CardGiftcardIcon sx={{ fontSize: 40, color: '#7c3aed' }} />
            </Box>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#111827', mb: 0.5 }}>
              No coupons yet
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#6b7280', mb: 2 }}>
              Earn points by placing orders, then redeem them for discount coupons.
            </Typography>
            <Button
              component={Link}
              to="/rewards"
              variant="contained"
              startIcon={<StarIcon />}
              sx={{
                bgcolor: '#FF3008', color: '#fff', fontWeight: 700,
                textTransform: 'none', borderRadius: '10px', px: 3, py: 1,
                '&:hover': { bgcolor: '#e02b00' },
              }}
            >
              Go to Rewards
            </Button>
          </Box>
        )}

        {/* Active section */}
        {(filter === 'ALL' || filter === 'ACTIVE') && buckets.active.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" gap={1.25} sx={{ mb: 2 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '12px',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(34,197,94,0.25)',
              }}>
                <CheckCircleOutlineIcon sx={{ color: '#fff', fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#111827', lineHeight: 1.2 }}>
                  Active coupons
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#6b7280' }}>
                  Apply any of these at checkout
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={2}>
              {buckets.active.map(c => <CouponTicket key={c._id} coupon={c} />)}
            </Stack>
          </Box>
        )}

        {/* Past section */}
        {(filter === 'ALL' || filter === 'PAST') && buckets.past.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" gap={1.25} sx={{ mb: 2 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '12px',
                bgcolor: '#f1f5f9', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <HistoryIcon sx={{ color: '#64748b', fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#111827', lineHeight: 1.2 }}>
                  Past coupons
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#6b7280' }}>
                  Used or expired — for your records
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={2}>
              {buckets.past.map(c => <CouponTicket key={c._id} coupon={c} />)}
            </Stack>
          </Box>
        )}

        {!loading && coupons.length > 0 && (
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button
              component={Link}
              to="/rewards"
              variant="outlined"
              startIcon={<StarIcon />}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: '12px',
                px: 3, py: 1, borderColor: '#FF3008', color: '#FF3008',
                '&:hover': { borderColor: '#FF3008', bgcolor: '#fef2f2' },
              }}
            >
              Earn more points
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default MyCoupons;
